import type { TenantUser } from '@gat-ship/types'
import { TenantUserUtils } from './tenant-user-utils'

/**
 * Comparator that orders tenant users active-first, then alphabetically by
 * `fullName` (null/undefined names sort as empty strings). Shared by the
 * collection sort and the select-options sort so the two stay in lock-step.
 */
export function compareTenantUsersActiveFirst(
  a: TenantUser,
  b: TenantUser
): number {
  const aIsActive = TenantUserUtils.isActiveTenantUser(a)
  const bIsActive = TenantUserUtils.isActiveTenantUser(b)

  // First sort by active status (active users first)
  if (aIsActive !== bIsActive) {
    return aIsActive ? -1 : 1
  }

  // Then sort alphabetically by fullName, handling null/undefined cases
  const aName = a.fullName || ''
  const bName = b.fullName || ''
  return aName.localeCompare(bName)
}

/**
 * Sorts a tenant-user collection active-first then alphabetically. Returns a
 * fresh array — the input is never mutated.
 */
export function sortTenantUsers(users: readonly TenantUser[]): TenantUser[] {
  return [...users].sort(compareTenantUsersActiveFirst)
}

/**
 * Filters a tenant-user collection for the list view: by tab, then by a
 * free-text search over `fullName`/`email`.
 *
 * @param activeTab - `active` keeps active or scheduled-for-activation users;
 *   `inactive` keeps the rest; any other value keeps all.
 * @param search - case-insensitive substring matched against name and email;
 *   empty leaves the tab result untouched.
 */
export function filterTenantUsersForList(
  users: readonly TenantUser[],
  { activeTab, search }: { activeTab: string; search: string }
): TenantUser[] {
  const byTab = users.filter((user) => {
    if (activeTab === 'active') {
      return (
        TenantUserUtils.isActiveTenantUser(user) ||
        TenantUserUtils.isScheduledForActivation(user)
      )
    }
    if (activeTab === 'inactive') {
      return (
        !TenantUserUtils.isActiveTenantUser(user) &&
        !TenantUserUtils.isScheduledForActivation(user)
      )
    }
    return true
  })

  if (!search) {
    return byTab
  }
  const query = search.toLowerCase()
  return byTab.filter(
    (user) =>
      user.fullName.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
  )
}

/**
 * Sorts a tenant-user collection by name for the list view. `default` preserves
 * the incoming order; `asc`/`desc` sort by `fullName`. Returns a fresh array.
 */
export function sortTenantUsersByName(
  users: readonly TenantUser[],
  sortBy: string
): TenantUser[] {
  if (sortBy === 'default') {
    return [...users]
  }
  return [...users].sort((a, b) =>
    sortBy === 'asc'
      ? a.fullName.localeCompare(b.fullName)
      : b.fullName.localeCompare(a.fullName)
  )
}

/**
 * Picks the tenant users to offer in a select: drops inactive users unless
 * `showInactive`, appends `preSelectedUser` when it is not already present, and
 * orders the result active-first then by name. Returns a fresh array.
 */
export function selectTenantUserOptions(
  users: readonly TenantUser[],
  {
    showInactive,
    preSelectedUser
  }: { showInactive: boolean; preSelectedUser?: TenantUser }
): TenantUser[] {
  const visible = showInactive
    ? [...users]
    : users.filter((user) => TenantUserUtils.isActiveTenantUser(user))

  const withPreSelected =
    preSelectedUser &&
    !visible.some((user) => user.userId === preSelectedUser.userId)
      ? [...visible, preSelectedUser]
      : visible

  return [...withPreSelected].sort(compareTenantUsersActiveFirst)
}

/**
 * Display title for a tenant user in a list/select. When more than one user
 * shares the same `fullName`, the initials are surfaced as a `shortName` to
 * disambiguate them.
 */
export function tenantUserDisplayTitle(
  user: Pick<TenantUser, 'fullName' | 'initials'>,
  allUsers: readonly Pick<TenantUser, 'fullName'>[]
): { name: string; shortName?: string } {
  const sameNameCount = allUsers.filter(
    (candidate) => candidate.fullName === user.fullName
  ).length
  if (sameNameCount > 1) {
    return { name: user.fullName, shortName: user.initials }
  }
  return { name: user.fullName }
}
